<template>
    <div class="v-atlas-storymap-main">

        <!-- Dynamic remote template (compiled at runtime) -->
        <template v-if="props.mapModule.moduleConfig.contentUrl">

            <div v-if="isLoading" class="v-atlas-storymap-main__loading">Lädt…</div>


            <component :is="DynamicStoryContent" v-else-if="DynamicStoryContent" />


            <div v-else-if="fetchError" class="v-atlas-storymap-main__error">{{ fetchError }}</div>
        </template>

        <div v-else>
            Inhalt konnte nicht geladen werden.
        </div>

    </div>
</template>

<script setup lang="ts">
//#region 3rd party imports
import { Collapsible, ViewStackController, ScrollSensor, ScrollSensorItem } from "gertrud";
import { compile, defineComponent, onMounted, onUnmounted, Ref, ref, shallowRef } from "vue";
import type { Component } from "vue";
import { StoryMapModule } from "./StoryMapModule";
//#endregion 3rd party imports

const props = withDefaults(defineProps<{
    mapModule: StoryMapModule,
    viewStackController: ViewStackController
}>(), {
});

const local: Ref<any> = ref({})

//#region Dynamic remote template
const DynamicStoryContent = shallowRef<Component | null>(null)
const isLoading = ref(false)
const fetchError = ref<string | null>(null)

/**
 * Fetches a Vue template string from the given URL, compiles it at runtime
 * (requires the vue/dist/vue.esm-bundler.js build which includes the compiler),
 * and stores the resulting component in `DynamicStoryContent`.
 */

// TODO: Use useSmartFetch() for this
async function fetchAndCompileTemplate(url: string): Promise<void> {
    isLoading.value = true
    fetchError.value = null


    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} ${response.statusText}`)
        }
        const templateString = await response.text()
        const renderFn = compile(templateString)

        // Capture the values the remote template may reference.
        // By returning them from setup(), they are available directly on _ctx
        // (i.e. as `mapModule`, `setLayerVisibility`, etc.) inside the
        // compiled render function — no `props.` prefix needed in the template.
        const mapModule = props.mapModule
        const viewStackController = props.viewStackController

        DynamicStoryContent.value = defineComponent({
            render: renderFn,
            components: { Collapsible, ScrollSensor, ScrollSensorItem },
            setup() {
                return {
                    mapModule,
                    viewStackController,
                    local,
                    isLoading,
                    setLayersVisibility,
                }
            },
        })
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        console.error('[StoryMapMain] Failed to load remote template:', message)
        fetchError.value = `Inhalt konnte nicht geladen werden: ${message}`
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    const url = props.mapModule.moduleConfig.contentUrl
    if (url) {
        fetchAndCompileTemplate(url)
    }
})
//#endregion Dynamic remote template

//#region Layer visibility
function setLayersVisibility(layerIds: Array<string>, visible: boolean) {

    for (const layerId of layerIds) {
        const prefixedId = props.mapModule.uuid + "_" + layerId
        props.mapModule.setLayerVisibilityState(prefixedId, visible)
    }

}
//#endregion Layer visibility

onUnmounted(() => {
    props.mapModule.setAllLayersVisibility(false)
})
</script>
